import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ArrowRight, Zap, Sparkles, Copy, Check } from 'lucide-react';

/**
 * Site-wide relaunch-sale announcement modal.
 *
 * Auto-opens on every visit / refresh of the home page ("/"). Dismissible via
 * the X button, backdrop click, or the Escape key. Re-opens the next time the
 * user lands on / (no localStorage suppression).
 */
export default function AnnouncementModal() {
  const { pathname } = useLocation();
  const nav = useNavigate();
  const [open, setOpen] = useState(() => pathname === '/');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (pathname === '/') setOpen(true);
  }, [pathname]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open]);

  const close = () => setOpen(false);
  const startEvaluation = () => { close(); setTimeout(() => nav('/plans'), 180); };

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText('TSGBACK');
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      /* clipboard perms may be denied — user can still type it in */
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 overflow-y-auto"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          onClick={close}
          data-testid="announcement-modal"
        >
          {/* Blurred backdrop */}
          <div className="absolute inset-0 bg-black/80 backdrop-blur-md" />

          {/* Ambient neon glow behind card */}
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[620px] h-[620px] rounded-full bg-[radial-gradient(circle,rgba(0,255,102,0.25),transparent_60%)] blur-2xl" />
          </div>

          <motion.div
            role="dialog"
            aria-modal="true"
            aria-labelledby="announcement-title"
            className="relative w-full max-w-[640px] my-auto rounded-2xl overflow-hidden border border-[#00ff66]/25 bg-[#050505] shadow-[0_25px_80px_-15px_rgba(0,255,102,0.4)]"
            initial={{ opacity: 0, scale: 0.92, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.94, y: 10 }}
            transition={{ type: 'spring', damping: 22, stiffness: 240 }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Neon top gradient bar */}
            <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[#00ff66] to-transparent" />

            {/* Grain / noise overlay */}
            <div className="absolute inset-0 pointer-events-none opacity-[0.05] mix-blend-overlay"
                 style={{ backgroundImage: 'url("data:image/svg+xml;utf8,<svg xmlns=%27http://www.w3.org/2000/svg%27 width=%27200%27 height=%27200%27><filter id=%27n%27><feTurbulence baseFrequency=%270.9%27 numOctaves=%272%27 stitchTiles=%27stitch%27/></filter><rect width=%27100%25%27 height=%27100%25%27 filter=%27url(%23n)%27/></svg>")' }} />

            {/* Close (X) */}
            <button
              onClick={close}
              aria-label="Close announcement"
              className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full flex items-center justify-center bg-white/[0.04] border border-white/10 text-zinc-400 hover:text-white hover:border-white/25 hover:bg-white/[0.08] transition"
              data-testid="announcement-close-btn"
            >
              <X size={16} />
            </button>

            <div className="relative p-6 sm:p-10">
              {/* Header — icon + eyebrow */}
              <div className="flex items-center gap-3 pr-10">
                <div className="w-11 h-11 rounded-full bg-[#00ff66]/10 border border-[#00ff66]/40 flex items-center justify-center shrink-0">
                  <Sparkles className="text-[#00ff66]" size={20} />
                </div>
                <div className="min-w-0">
                  <div className="font-mono text-[10px] tracking-[3px] text-[#00ff66] uppercase">
                    Traders Spring · Relaunch Sale
                  </div>
                  <div className="text-xs text-zinc-500 mt-0.5">We're back. So are the discounts.</div>
                </div>
              </div>

              {/* Divider */}
              <div className="my-6 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />

              {/* Title */}
              <h2
                id="announcement-title"
                className="font-display text-3xl sm:text-4xl leading-tight tracking-tight"
              >
                Welcome back — enjoy
                <span className="text-[#00ff66]"> up to 50% off.</span>
              </h2>
              <p className="mt-2 text-sm text-zinc-500">
                Use code <span className="font-mono text-white">TSGBACK</span> at checkout to unlock the relaunch discount.
              </p>

              {/* Discount grid */}
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="discount-grid">
                <OfferCard
                  eyebrow="One-Step Program"
                  headline="50% OFF"
                  subline="Entry fee"
                  detail="$20 → $10 · unlock fee unchanged"
                />
                <OfferCard
                  eyebrow="Instant Funding"
                  headline="20% OFF"
                  subline="Access fee"
                  detail="Every account size, all currencies"
                />
              </div>

              {/* Promo code copy chip */}
              <div className="mt-5 flex items-center justify-between rounded-xl border border-[#00ff66]/30 bg-[#00ff66]/[0.06] px-4 py-3">
                <div className="flex items-center gap-3 min-w-0">
                  <Zap size={16} className="text-[#00ff66] shrink-0" />
                  <div className="min-w-0">
                    <div className="font-mono text-[10px] tracking-[3px] text-[#00ff66] uppercase">Promo code</div>
                    <div className="font-mono text-xl tracking-[4px] text-white">TSGBACK</div>
                  </div>
                </div>
                <button
                  onClick={copyCode}
                  className="btn-ghost btn-sm inline-flex items-center gap-2"
                  data-testid="announcement-copy-code-btn"
                >
                  {copied ? <Check size={13} /> : <Copy size={13} />}
                  {copied ? 'Copied' : 'Copy'}
                </button>
              </div>

              {/* Fine print */}
              <p className="mt-4 text-xs text-zinc-500 leading-relaxed">
                Applies to program entry only. The One-Step unlock fee (paid after passing the 15% objective) is not discounted. No expiry — pulled when the sale ends.
              </p>

              {/* CTA row */}
              <div className="mt-7 flex flex-col sm:flex-row gap-3">
                <button
                  onClick={startEvaluation}
                  className="btn-primary btn-sm inline-flex items-center justify-center gap-2"
                  data-testid="announcement-start-btn"
                >
                  Start Evaluation <ArrowRight size={13} />
                </button>
                <button
                  onClick={close}
                  className="btn-ghost btn-sm sm:ml-auto"
                  data-testid="announcement-dismiss-btn"
                >
                  Maybe later
                </button>
              </div>
            </div>

            {/* Bottom fine print */}
            <div className="border-t border-white/5 bg-black/50 px-8 py-4 text-[10px] font-mono tracking-[2px] text-zinc-500 uppercase text-center">
              Simulated evaluation environment · Not a broker · No client funds held
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function OfferCard({ eyebrow, headline, subline, detail }) {
  return (
    <div className="rounded-xl border border-white/10 bg-[#0a0a0a] p-4 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-[#00ff66]/5 via-transparent to-transparent pointer-events-none" />
      <div className="relative">
        <div className="font-mono text-[10px] tracking-[2px] uppercase text-zinc-400">{eyebrow}</div>
        <div className="mt-2 font-display text-3xl text-[#00ff66] tracking-tight">{headline}</div>
        <div className="text-xs text-zinc-400 mt-0.5">{subline}</div>
        <div className="text-[11px] text-zinc-500 mt-2">{detail}</div>
      </div>
    </div>
  );
}
